import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  X,
  Users,
  GraduationCap,
  BookOpen,
  Calendar,
  DoorOpen,
  School,
  LayoutDashboard,
  Send,
  Library,
  MessageCircle,
  Wallet,
  Shield,
  Clock,
  UserX,
  ChevronDown,
  Download, TrashIcon
} from "lucide-react";

const SECTIONS = [
  {
    label: null, // no section header for the first item
    items: [
      { name: "Tableau de bord", href: "/", icon: LayoutDashboard },
    ],
  },
  {
    label: "Gestion",
    items: [
      { name: "École",       href: "/ecole",       icon: School },
      { name: "Années Scolaires", href: "/annees", icon: Calendar },
    ],
  },
  {
    label: "Compte",
    items: [
      { name: "Étudiants",   href: "/students",  icon: GraduationCap },
      { name: "Professeurs", href: "/teachers",  icon: Users },
      { name: "Administrateurs", href: "/admins", icon: Shield },
      { name: "Export Données", href: "/export", icon: Download },
      { name: "Demandes suppression", href: "/deletion-requests", icon: TrashIcon },
    ],
  },
  {
    label: "Scolarité",
    items: [
      { name: "Classes",     href: "/classes",   icon: Library },
      { name: "Matières",    href: "/matieres",  icon: BookOpen },
      { name: "Salles",      href: "/salles",    icon: DoorOpen },
      { name: "Périodes",    href: "/periods",   icon: Clock },
      { name: "Plannings",   href: "/plannings", icon: Calendar },
      { name: "Coefficients",href: "/coefficients", icon: BookOpen },
      { name: "Notes", href: "/notes", icon: BookOpen },
      { name: "Bulletins",   href: "/bulletins", icon: BookOpen },
      { name: "Absences",    href: "/absences",  icon: UserX },
    ],
  },
  {
    label: "Communication",
    items: [
      { name: "Messagerie",        href: "/chats",         icon: MessageCircle },
      { name: "Notifications/SMS", href: "/notifications", icon: Send },
      { name: "Réunions",          href: "/reunions",      icon: Users },
      // { name: "Informations",      href: "/infos",         icon: Info },
      // { name: "Publications",      href: "/pubs",          icon: Image },
    ],
  },
  {
    label: "Finances",
    items: [
      { name: "Scolarité / Finances", href: "/finances", icon: Wallet },
    ],
  }
];

const SUPER_ADMIN_SECTIONS = [
  {
    label: null,
    items: [
      { name: "Vue d'ensemble", href: "/", icon: LayoutDashboard },
    ],
  },
  {
    label: "Gestion Globale",
    items: [
      { name: "Écoles", href: "/super-admin/ecoles", icon: School },
      { name: "Administrateurs", href: "/super-admin/admins", icon: Shield },
    ],
  },
  {
    label: "Communication",
    items: [
      { name: "Notifications Globales", href: "/super-admin/notifications", icon: Send },
    ],
  }
];

function NavSection({ section, location, setSidebarOpen }) {
  const [collapsed, setCollapsed] = useState(false);

  const hasActive = section.items.some(
    (item) =>
      location.pathname === item.href ||
      (item.href !== "/" && location.pathname.startsWith(item.href))
  );

  return (
    <div className="mb-1">
      {section.label && (
        <button
          onClick={() => setCollapsed((v) => !v)}
          className="w-full flex items-center justify-between px-3 py-1.5 mb-0.5 group"
        >
          <span className={`text-[10px] font-bold uppercase tracking-widest transition-colors ${
            hasActive ? "text-indigo-500" : "text-gray-400 group-hover:text-gray-500"
          }`}>
            {section.label}
          </span>
          <ChevronDown
            className={`w-3 h-3 text-gray-300 transition-transform duration-200 ${
              collapsed ? "-rotate-90" : "rotate-0"
            }`}
          />
        </button>
      )}

      {!collapsed && (
        <div className="space-y-0.5">
          {section.items.map((item) => {
            const isActive =
              location.pathname === item.href ||
              (item.href !== "/" && location.pathname.startsWith(item.href));
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
                  isActive
                    ? "bg-indigo-50 text-indigo-700"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                <Icon
                  className={`h-4.5 w-4.5 flex-shrink-0 transition-colors ${
                    isActive ? "text-indigo-600" : "text-gray-400 group-hover:text-gray-600"
                  }`}
                  style={{ width: "1.125rem", height: "1.125rem" }}
                />
                <span>{item.name}</span>
                {isActive && (
                  <span className="ml-auto w-1.5 h-1.5 rounded-full bg-indigo-500" />
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ViewSidebar({ sidebarOpen, setSidebarOpen }) {
  const location = useLocation();
  const user = useSelector((state) => state.userReducer?.user);
  
  const activeSections = user?.role === "SUPER_ADMIN" ? SUPER_ADMIN_SECTIONS : SECTIONS;

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/50 backdrop-blur-sm lg:hidden transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 transform bg-white border-r border-gray-200 transition-transform duration-300 ease-in-out lg:static lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full"
        }`}
      >
        {/* Logo */}
        <div className="flex h-16 shrink-0 items-center justify-between px-6 bg-white border-b border-gray-100">
          <Link
            to="/"
            className="flex items-center gap-3 font-bold text-xl tracking-tight text-gray-900 focus:outline-none"
          >
            <img 
              src="/assets/images/logo.png" 
              alt="Kalan Yoro Logo" 
              className="h-10 w-auto object-contain drop-shadow-sm" 
            />
          </Link>
          <button
            className="lg:hidden p-2 -mr-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="h-[calc(100%-4rem)] overflow-y-auto px-3 py-4 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
          {activeSections.map((section, i) => (
            <NavSection
              key={i}
              section={section}
              location={location}
              setSidebarOpen={setSidebarOpen}
            />
          ))}

          {/* Bottom divider + version */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <p className="text-[10px] text-gray-300 text-center tracking-wide">
              KalanYoro Admin · v1.0
            </p>
          </div>
        </nav>
      </aside>
    </>
  );
}